import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { useSubscription } from "@/context/SubscriptionContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { api } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Mail, RotateCw } from "lucide-react";
import GoogleButton from "@/components/GoogleButton";
import LegalStrip from "@/components/LegalStrip";
import { tokenStorage } from "@/lib/tokenStorage";

export default function Login() {
  const { login, loginTwoFactor, user, refreshUser } = useAuth();
  const { refresh: refreshSub } = useSubscription();
  const { t } = useI18n();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [needVerify, setNeedVerify] = useState(false);
  const [code, setCode] = useState("");
  const [resending, setResending] = useState(false);
  const [need2FA, setNeed2FA] = useState(false);
  const [twoCode, setTwoCode] = useState("");

  if (user) return <Navigate to="/dashboard" replace />;

  const finish = async () => {
    try {
      const r = await api.get("/billing/subscription");
      nav(r.data?.has_active ? "/dashboard" : "/pricing");
      await refreshSub();
    } catch {
      nav("/pricing");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res?.two_factor_required) {
        setNeed2FA(true);
        toast.info("E-postanıza gönderilen 6 haneli kodu girin");
      } else {
        await finish();
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "email_not_verified") {
        // Auto-send fresh code
        try { await api.post("/auth/resend-verification", { email: d.email || email }); } catch {}
        toast.info(d.message || t("verify.subtitle"));
        setNeedVerify(true);
      } else {
        toast.error(typeof d === "string" ? d : (d?.message || "Giriş başarısız"));
      }
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/auth/verify-email", { email, code: code.trim() });
      tokenStorage.set("uc_token", r.data.access_token);
      await refreshUser();
      // Verified users have no subscription yet → send them to pricing
      nav("/pricing");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { email });
      toast.success(t("verify.resent"));
    } catch {} finally { setResending(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      <div className="hidden lg:flex flex-col justify-between p-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-40" style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1583902340370-6a3fc847d373?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHw0fHxnb2xkJTIwY29pbiUyMHN0YWNrfGVufDB8fHx8MTc4NTc1MDE5OXww&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover", backgroundPosition: "center",
        }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(5,5,5,0.85), rgba(5,5,5,0.6) 60%, rgba(5,5,5,0.95))" }} />
        <div className="relative z-10">
          <div className="font-display text-4xl tracking-widest" style={{color:"var(--gold)"}}>MİLYEMSOFT</div>
          <div className="label-caps mt-2">{t("app.tagline")}</div>
        </div>
        <div className="relative z-10 max-w-md space-y-6">
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight font-light">
            {t("login.headline1")}<br /><span style={{color:"var(--gold)"}}>{t("login.headline2")}</span> {t("login.headline3")}
          </h1>
          <p className="text-neutral-400 leading-relaxed">{t("login.subline")}</p>
          <div className="label-caps text-neutral-500 pt-2">{t("footer.trust")}</div>
        </div>
        <div className="relative z-10 label-caps">{t("footer.copyright")}</div>
      </div>

      <div className="flex flex-col lg:items-center lg:justify-center p-6 sm:p-8 lg:p-16 bg-[#0a0a0a] relative min-h-screen">
        <div className="absolute right-4 sm:right-6 z-20 safe-top-abs"><LanguageSwitcher compact /></div>
        <div className="lg:hidden absolute left-4 sm:left-6 z-20 safe-top-abs">
          <div className="font-display text-xl tracking-widest" style={{color:"var(--gold)"}}>MİLYEMSOFT</div>
        </div>
        <div className="order-last lg:order-none lg:absolute lg:bottom-0 lg:left-0 lg:right-0 lg:px-8 mt-auto pt-8 w-full"><LegalStrip /></div>

        {need2FA ? (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setLoading(true);
              try {
                await loginTwoFactor(email, twoCode);
                await finish();
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Kod hatalı");
              } finally { setLoading(false); }
            }}
            className="w-full max-w-sm space-y-5 safe-top-form"
            data-testid="login-2fa-form"
          >
            <div>
              <div className="w-12 h-12 border flex items-center justify-center mb-4" style={{borderColor:"var(--gold)", color:"var(--gold)"}}>🔒</div>
              <div className="label-caps">Giriş Güvenliği</div>
              <h2 className="font-display text-3xl mt-2">İki Faktörlü Doğrulama</h2>
              <p className="text-xs text-neutral-400 mt-2">E-postanıza gönderilen 6 haneli kodu girin.</p>
              <p className="text-sm mt-3 font-mono" style={{color:"var(--gold)"}}>{email}</p>
            </div>
            <div>
              <label className="label-caps block mb-2">Doğrulama Kodu</label>
              <input
                required autoFocus inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                value={twoCode}
                onChange={(e)=>setTwoCode(e.target.value.replace(/[^0-9]/g,""))}
                data-testid="login-2fa-code"
                className="w-full bg-[--surface] border border-white/10 px-4 py-4 text-center text-2xl font-mono tracking-[0.5em] focus:outline-none focus:ring-1 focus:ring-[--gold]"
                style={{background:"var(--surface)"}}
                placeholder="000000"
              />
            </div>
            <button type="submit" disabled={loading || twoCode.length !== 6} className="btn-primary-gold w-full" data-testid="login-2fa-submit">
              {loading ? "Doğrulanıyor…" : "Girişi Tamamla"}
            </button>
            <div className="flex items-center justify-between text-xs pt-2 border-t border-white/8">
              <button type="button" onClick={()=>{ setNeed2FA(false); setTwoCode(""); }} className="text-neutral-500 hover:text-white">
                ← Farklı hesap
              </button>
            </div>
            <div className="text-[11px] text-neutral-600 leading-relaxed">Kod 10 dakika geçerlidir. Gelmediyse Spam / Öne Çıkanlar kutusuna bakın.</div>
          </form>
        ) : !needVerify ? (
          <form onSubmit={submit} className="w-full max-w-sm space-y-6 safe-top-form" data-testid="login-form">
            <div>
              <div className="label-caps">{t("auth.session")}</div>
              <h2 className="font-display text-3xl mt-2">{t("auth.login_title")}</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="label-caps block mb-2">{t("auth.email")}</label>
                <input data-testid="login-email" type="email" inputMode="email" value={email} onChange={(e)=>setEmail(e.target.value)} required autoComplete="email"
                  className="w-full bg-[--surface] border border-white/10 px-3 py-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[--gold]" style={{background: "var(--surface)"}} />
              </div>
              <div>
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 mb-2">
                  <label className="label-caps">{t("auth.password")}</label>
                  <Link to="/forgot-password" className="text-[10px] sm:text-[11px] tracking-[0.18em] uppercase hover:text-[--gold] whitespace-nowrap" style={{color:"var(--gold)"}} data-testid="link-forgot">{t("forgot.link")}</Link>
                </div>
                <input data-testid="login-password" type="password" value={password} onChange={(e)=>setPassword(e.target.value)} required autoComplete="current-password"
                  className="w-full bg-[--surface] border border-white/10 px-3 py-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[--gold]" style={{background: "var(--surface)"}} />
              </div>
            </div>

            <button type="submit" disabled={loading} data-testid="login-submit" className="btn-primary-gold w-full">
              {loading ? t("auth.connecting") : t("auth.login")}
            </button>

            <div className="relative flex items-center py-1">
              <div className="flex-1 border-t border-white/10"></div>
              <span className="px-3 text-[10px] uppercase tracking-widest text-neutral-600">{t("auth.or_divider")}</span>
              <div className="flex-1 border-t border-white/10"></div>
            </div>

            <GoogleButton testId="google-signin-btn-login" />

            <div className="text-center text-sm text-neutral-500 pt-4 border-t border-white/8 leading-relaxed">
              {t("auth.no_account")}{" "}
              <Link to="/signup" data-testid="link-signup" className="hover:text-[--gold] whitespace-nowrap" style={{color:"var(--gold)"}}>{t("auth.register_shop")}</Link>
            </div>
          </form>
        ) : (
          <form onSubmit={verify} className="w-full max-w-sm space-y-5 pt-12 lg:pt-0" data-testid="login-verify-form">
            <div>
              <div className="w-12 h-12 border flex items-center justify-center mb-4" style={{borderColor:"var(--gold)", color:"var(--gold)"}}><Mail size={22}/></div>
              <div className="label-caps">{t("auth.session")}</div>
              <h2 className="font-display text-3xl mt-2">{t("verify.title")}</h2>
              <p className="text-xs text-neutral-400 mt-2">{t("verify.subtitle")}</p>
              <p className="text-sm mt-3 font-mono" style={{color:"var(--gold)"}}>{email}</p>
            </div>
            <div>
              <label className="label-caps block mb-2">{t("verify.code_label")}</label>
              <input required autoFocus inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                value={code} onChange={(e)=>setCode(e.target.value.replace(/[^0-9]/g,""))}
                data-testid="login-verify-code"
                className="w-full bg-[--surface] border border-white/10 px-4 py-4 text-center text-2xl font-mono tracking-[0.5em] focus:outline-none focus:ring-1 focus:ring-[--gold]"
                style={{background:"var(--surface)"}} placeholder="000000" />
            </div>
            <button type="submit" disabled={loading || code.length !== 6} className="btn-primary-gold w-full" data-testid="login-verify-submit">
              {loading ? t("auth.connecting") : t("verify.submit")}
            </button>
            <div className="flex items-center justify-between text-xs pt-2 border-t border-white/8">
              <button type="button" onClick={resend} disabled={resending} className="flex items-center gap-1.5 text-neutral-400 hover:text-[--gold]">
                <RotateCw size={12} className={resending?"animate-spin":""}/> {t("verify.resend")}
              </button>
              <button type="button" onClick={()=>setNeedVerify(false)} className="text-neutral-500 hover:text-white">{t("verify.change_email")}</button>
            </div>
            <div className="text-[11px] text-neutral-600 leading-relaxed">{t("verify.spam_hint")}</div>
          </form>
        )}
      </div>
      <Toaster theme="dark" position="top-right" />
    </div>
  );
}
